<template>
  <div class="space-y-3">
    <!-- Running Projects -->
    <div class="border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 p-2">
      <h6
        class="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1 cursor-pointer select-none"
        @click="projectsExpanded = !projectsExpanded"
      >
        <ChevronDownIcon
          class="h-3 w-3 text-gray-400 dark:text-gray-500 transition-transform"
          :class="{ '-rotate-90': !projectsExpanded }"
        />
        <CheckCircleIcon class="h-3 w-3 text-green-600 dark:text-green-500" />
        Lopende Projecten
        <span
          v-if="!isLoadingProjects && projects.length > 0"
          class="font-normal text-gray-400 dark:text-gray-500 ml-1"
          >({{ projects.length }})</span
        >
        <span
          v-if="!isLoadingProjects && projects.length === 0"
          class="font-normal text-gray-400 dark:text-gray-500 italic ml-1"
          >— geen</span
        >
      </h6>
      <div v-show="projectsExpanded">
        <div v-if="isLoadingProjects" class="text-center py-2">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mx-auto"></div>
        </div>
        <div v-else-if="projects.length > 0" class="space-y-1 mt-2">
          <div
            v-for="(proj, idx) in projects"
            :key="idx"
            class="text-xs p-1.5 rounded border bg-blue-50 dark:bg-blue-900/30 border-blue-100 dark:border-blue-800"
          >
            <div class="font-medium text-gray-900 dark:text-white truncate" :title="proj.name">
              {{ proj.name }}
            </div>
            <div
              v-if="proj.organization_name"
              class="text-gray-500 dark:text-gray-400 truncate text-[10px]"
              :title="proj.organization_name"
            >
              {{ proj.organization_name }}
            </div>
            <div v-if="proj.lastDate" class="text-gray-500 dark:text-gray-400">
              tot {{ formatDateFull(proj.lastDate) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pre-sourcings -->
    <div class="border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 p-2">
      <h6
        class="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1 cursor-pointer select-none"
        @click="assignmentsExpanded = !assignmentsExpanded"
      >
        <ChevronDownIcon
          class="h-3 w-3 text-gray-400 dark:text-gray-500 transition-transform"
          :class="{ '-rotate-90': !assignmentsExpanded }"
        />
        <CalendarIcon class="h-3 w-3 text-purple-600 dark:text-purple-400" />
        Pre-sourcings
        <span
          v-if="!isLoadingAssignments && assignments.length > 0"
          class="font-normal text-gray-400 dark:text-gray-500 ml-1"
          >({{ assignments.length }})</span
        >
        <span
          v-if="!isLoadingAssignments && assignments.length === 0"
          class="font-normal text-gray-400 dark:text-gray-500 italic ml-1"
          >— geen</span
        >
      </h6>
      <div v-show="assignmentsExpanded">
        <div v-if="isLoadingAssignments" class="text-center py-2">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mx-auto"></div>
        </div>
        <div v-else-if="assignments.length > 0" class="space-y-1 mt-2">
          <div
            v-for="assignment in assignments"
            :key="assignment.id"
            class="text-xs p-1.5 rounded border"
            :class="
              isAssignmentActive(assignment)
                ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-100 dark:border-purple-800'
                : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
            "
          >
            <div
              class="font-medium text-gray-900 dark:text-white truncate"
              :title="assignment.prospect_subject"
            >
              {{ assignment.prospect_subject }}
            </div>
            <div
              v-if="assignment.role"
              class="text-purple-700 dark:text-purple-300 truncate text-[10px] font-medium"
              :title="assignment.role"
            >
              Rol: {{ assignment.role }}
            </div>
            <div
              v-if="assignment.organization_name"
              class="text-gray-500 dark:text-gray-400 truncate text-[10px]"
            >
              {{ assignment.organization_name }}
            </div>
            <div class="text-gray-500 dark:text-gray-400 mt-0.5">
              {{ assignment.dpw }}d/w
              <span v-if="assignment.chance_to_score != null">
                • {{ assignment.chance_to_score }}% kans
              </span>
            </div>
            <div class="text-gray-500 dark:text-gray-400 text-[10px]">
              {{ formatDateFull(assignment.start_date) }} -
              {{ formatDateFull(assignment.end_date) }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Aanvragen (Voorgesteld) -->
    <div class="border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 p-2">
      <h6
        class="text-xs font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1 cursor-pointer select-none"
        @click="proposalsExpanded = !proposalsExpanded"
      >
        <ChevronDownIcon
          class="h-3 w-3 text-gray-400 dark:text-gray-500 transition-transform"
          :class="{ '-rotate-90': !proposalsExpanded }"
        />
        <ClipboardDocumentIcon class="h-3 w-3 text-yellow-500 dark:text-yellow-400" />
        Aanvragen
        <span
          v-if="!isLoadingProposals && proposals.length > 0"
          class="font-normal text-gray-400 dark:text-gray-500 ml-1"
          >({{ proposals.length }})</span
        >
        <span
          v-if="!isLoadingProposals && proposals.length === 0"
          class="font-normal text-gray-400 dark:text-gray-500 italic ml-1"
          >— geen</span
        >
      </h6>
      <div v-show="proposalsExpanded">
        <div v-if="isLoadingProposals" class="text-center py-2">
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-500 mx-auto"></div>
        </div>
        <div v-else-if="proposals.length > 0" class="space-y-1 mt-2">
          <div
            v-for="(proposal, idx) in proposals"
            :key="proposal.id ?? `local-${idx}`"
            class="text-xs p-1.5 rounded border"
            :class="
              isProposalActive(proposal)
                ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-100 dark:border-yellow-800'
                : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
            "
          >
            <div
              class="font-medium text-gray-900 dark:text-white truncate flex items-center gap-1"
              :title="proposal.prospect_subject"
            >
              <span class="truncate">{{ proposal.prospect_subject }}</span>
              <span
                v-if="proposal.is_local"
                class="text-[10px] uppercase tracking-wide text-yellow-700 dark:text-yellow-300 flex-shrink-0"
                >concept</span
              >
            </div>
            <div
              v-if="proposal.role"
              class="text-yellow-700 dark:text-yellow-300 truncate text-[10px] font-medium"
              :title="proposal.role"
            >
              Rol: {{ proposal.role }}
            </div>
            <div
              v-if="proposal.organization_name"
              class="text-gray-500 dark:text-gray-400 truncate text-[10px]"
            >
              {{ proposal.organization_name }}
            </div>
            <div class="text-gray-500 dark:text-gray-400 mt-0.5">
              {{ proposal.pl_selected_dpw }}d/w
              <span v-if="proposal.chance_to_score != null">
                • {{ proposal.chance_to_score }}% kans
              </span>
            </div>
            <div class="text-gray-500 dark:text-gray-400 text-[10px]">
              {{ formatDateFull(proposal.effective_start_date) }} -
              {{ formatDateFull(proposal.effective_end_date) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import {
  ChevronDownIcon,
  CheckCircleIcon,
  CalendarIcon,
  ClipboardDocumentIcon
} from '@heroicons/vue/24/outline'
import { hoursService } from '@/services/hoursService.js'
import {
  prospectAssignmentService,
  prospectPreferencesService
} from '@/services/prospectService.js'

const props = defineProps({
  employeeId: {
    type: String,
    required: true
  },
  periodStart: {
    type: String,
    default: null
  },
  periodEnd: {
    type: String,
    default: null
  },
  // Locally-staged proposals to show in the Aanvragen section in addition
  // to the server-side ones. Each entry expects: prospect_subject,
  // organization_name, role, pl_selected_dpw, effective_start_date,
  // effective_end_date, is_local.
  extraProposals: {
    type: Array,
    default: () => []
  },
  // Preference whose proposal should be hidden from the Aanvragen section.
  // Used on Concretiseren where the same role is already shown as the grey
  // potential bar in the chart (prevents showing the role twice).
  excludeProposedPreferenceId: {
    type: [Number, String],
    default: null
  }
})

// State
const projects = ref([])
const assignments = ref([])
const serverProposals = ref([])
const isLoadingProjects = ref(false)
const isLoadingAssignments = ref(false)
const isLoadingProposals = ref(false)

// Combined list: server-side proposals + locally-staged ones (concepts).
// Excludes the current preference's proposal when excludeProposedPreferenceId is set.
const proposals = computed(() => {
  const excludeId =
    props.excludeProposedPreferenceId != null ? Number(props.excludeProposedPreferenceId) : null
  const server =
    excludeId != null
      ? serverProposals.value.filter(p => Number(p.id) !== excludeId)
      : serverProposals.value
  return [...server, ...(props.extraProposals || [])]
})

// Collapse state - default expanded
const projectsExpanded = ref(true)
const assignmentsExpanded = ref(true)
const proposalsExpanded = ref(true)

// Load projects for employee
const loadProjects = async () => {
  if (!props.employeeId || !props.periodStart || !props.periodEnd) {
    projects.value = []
    return
  }

  isLoadingProjects.value = true
  try {
    const rows = await hoursService.getHours({
      employee_id: props.employeeId,
      start_date: props.periodStart,
      end_date: props.periodEnd,
      limit: 5000
    })

    // Group by project - only include Projectmanagement hours
    const projectMap = new Map()
    for (const row of rows) {
      // Only include Projectmanagement type
      const typeLabel = (row.type_label || '').toLowerCase()
      if (typeLabel !== 'projectmanagement') continue
      if (!row.project_name) continue

      const name = row.project_name
      const existing = projectMap.get(name) || {
        name,
        organization_name: row.organization_name || '',
        hours: 0,
        lastDate: null
      }
      if (!existing.organization_name && row.organization_name) {
        existing.organization_name = row.organization_name
      }
      existing.hours += Number(row.hours || 0)
      const rowDate = String(row.start_date).split('T')[0]
      if (!existing.lastDate || rowDate > existing.lastDate) {
        existing.lastDate = rowDate
      }
      projectMap.set(name, existing)
    }

    projects.value = [...projectMap.values()].sort((a, b) => {
      // Sort by lastDate descending (most recent end date first)
      if (a.lastDate && b.lastDate) {
        return b.lastDate.localeCompare(a.lastDate)
      }
      if (a.lastDate) return -1
      if (b.lastDate) return 1
      return 0
    })
  } catch (e) {
    console.error('Failed to load projects for employee:', props.employeeId, e)
    projects.value = []
  } finally {
    isLoadingProjects.value = false
  }
}

// Load assignments for employee
const loadAssignments = async () => {
  if (!props.employeeId) {
    assignments.value = []
    return
  }

  isLoadingAssignments.value = true
  try {
    const data = await prospectAssignmentService.getByEmployee(props.employeeId)
    // Sort by end date descending (most recent end date first)
    assignments.value = data.sort((a, b) => {
      if (a.end_date && b.end_date) {
        return b.end_date.localeCompare(a.end_date)
      }
      if (a.end_date) return -1
      if (b.end_date) return 1
      return 0
    })
  } catch (e) {
    console.error('Failed to load assignments for employee:', props.employeeId, e)
    assignments.value = []
  } finally {
    isLoadingAssignments.value = false
  }
}

// Load proposed allocations for employee
const loadProposals = async () => {
  if (!props.employeeId) {
    serverProposals.value = []
    return
  }

  isLoadingProposals.value = true
  try {
    serverProposals.value = await prospectPreferencesService.getProposedByEmployee(props.employeeId)
  } catch (e) {
    console.error('Failed to load proposals for employee:', props.employeeId, e)
    serverProposals.value = []
  } finally {
    isLoadingProposals.value = false
  }
}

// Check if assignment is active
const isAssignmentActive = assignment => {
  const today = new Date().toISOString().split('T')[0]
  return assignment.end_date >= today
}

// Check if proposal is active
const isProposalActive = proposal => {
  const today = new Date().toISOString().split('T')[0]
  const endDate = proposal.effective_end_date || proposal.end_date
  return endDate >= today
}

// Format date with year (dd-mm-yyyy)
const formatDateFull = input => {
  if (!input) return ''
  const s = String(input).split('T')[0]
  const d = new Date(`${s}T00:00:00`)
  if (isNaN(d)) return s
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
}

// Watch for changes and reload data
watch(
  () => [props.employeeId, props.periodStart, props.periodEnd],
  () => {
    loadProjects()
    loadAssignments()
    loadProposals()
  },
  { immediate: true }
)
</script>
